import type { User } from "@/types/Sidebar.type";
import type { Study as ApiStudy } from "@/pages/StudyManagement/types/study.type";
import type { Study as ContextStudy } from "@/pages/StudyManagement/types/studyManagement.types";
import { UserRoleEnum, isRole, isAnyRole } from "@/types/role.type";
import { STUDY_STATUSES, normalizeStudyStatus } from "@/types/studyStatus.type";

export const STUDY_CONTEXT_REFRESH_EVENT = "studyContextRefresh";
export const STUDY_CHANGED_EVENT = "studyChanged";

/**
 * Sentinel value used when a PM or CTA user has no study selected.
 * Selecting this triggers the restricted (no-study) menu for those roles.
 */
export const NO_STUDY_SENTINEL = "__no_study__";

/** Roles that are allowed to operate without a study assignment */
export const PLATFORM_ONLY_ROLES: readonly UserRoleEnum[] = [
  UserRoleEnum.PROJECT_MANAGER,
  UserRoleEnum.CTA,
] as const;

export function isPlatformOnlyRole(role: string | undefined): boolean {
  if (!role) {
    return false;
  }
  return isAnyRole(role, UserRoleEnum.PROJECT_MANAGER, UserRoleEnum.CTA);
}

/** Returns true if the given role string is the Project Manager platform role */
export function isPmRole(role: string | undefined): boolean {
  if (!role) {
    return false;
  }
  return isRole(role, UserRoleEnum.PROJECT_MANAGER);
}

/**
 * Returns true if the user has a PM or CTA role in ANY assignment
 * (platform-level OR study-scoped). This covers both "new" platform users
 * and "old" users whose PM/CTA role is tied to a specific studyId.
 */
export function isUserPmOrCta(user: User): boolean {
  return user.roles?.some((r) => isPlatformOnlyRole(r.role)) ?? false;
}

/**
 * Returns the first PM or CTA role name found on the user,
 * regardless of whether it is platform-level or study-scoped.
 */
export function getAnyPmOrCtaRole(user: User): string | undefined {
  return user.roles?.find((r) => isPlatformOnlyRole(r.role))?.role;
}

export interface StudyContextRefreshDetail {
  /** Study to select in the header context after refresh */
  selectStudyId?: string;
  /** Append a study-scoped role to the cached user (e.g. after PM creates a study) */
  appendRoleForStudy?: { studyId: string; role: string };
}

export function mapApiStudyToContextStudy(study: ApiStudy): ContextStudy {
  const status = normalizeStudyStatus(study.status ?? STUDY_STATUSES.DRAFT);

  return {
    id: study.id,
    protocolNumber: study.protocolNumber || "",
    title: study.title,
    principalInvestigator: study.principalInvestigator ?? "",
    startDate: study.startDate || "",
    endDate: study.endDate || "",
    status,
    phase: study.phase,
    therapeuticArea: study.therapeuticArea || "",
    sponsorName: study.sponsorName,
    siteAssignments: study.siteAssignments || [],
  };
}

/** Platform-level assignment (Scenario 3): role without studyId — tenant-wide access */
export function getPlatformRoleName(user: User): string | undefined {
  const platform = user.roles?.find((r) => !r.studyId && r.role);
  return platform?.role;
}

export function buildUserStudyRolesMap(
  user: User,
  studiesList: ApiStudy[],
): Record<string, string> {
  const roles: Record<string, string> = {};

  user.roles?.forEach((userRole) => {
    if (userRole.studyId && userRole.role) {
      roles[userRole.studyId] = userRole.role;
    }
  });

  const platformRole = getPlatformRoleName(user);
  if (platformRole) {
    for (const study of studiesList) {
      if (study.id && !roles[study.id]) {
        roles[study.id] = platformRole;
      }
    }
  }

  return roles;
}

export function buildAvailableContextStudies(
  studiesList: ApiStudy[],
  userStudyRoles: Record<string, string>,
  user: User,
): ContextStudy[] {
  const platformRole = getPlatformRoleName(user);

  if (platformRole) {
    return studiesList.map(mapApiStudyToContextStudy);
  }

  return studiesList
    .filter((study) => study.id && userStudyRoles[study.id])
    .map(mapApiStudyToContextStudy);
}

export function appendStudyRoleToUser(
  user: User,
  studyId: string,
  role: string,
): User {
  const roles = [...(user.roles || [])];
  if (!roles.some((r) => r.studyId === studyId)) {
    roles.push({ studyId, role });
  }
  return { ...user, roles };
}

export function dispatchStudyContextRefresh(
  detail: StudyContextRefreshDetail,
): void {
  window.dispatchEvent(
    new CustomEvent<StudyContextRefreshDetail>(STUDY_CONTEXT_REFRESH_EVENT, {
      detail,
    }),
  );
}

export function dispatchStudyChanged(): void {
  window.dispatchEvent(new Event(STUDY_CHANGED_EVENT));
}
